import { useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "./ui/dialog";
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from "./ui/tooltip";
import { toast } from "sonner";
import {
  SlidersHorizontal, Mail, MailX, Globe, GlobeLock, Phone as PhoneIcon,
  UserX, UserCheck, X, Save, Bookmark, Trash2, Calendar as CalendarIcon,
  Share2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SORT_OPTIONS = [
  { value: "created_desc",  label: "Newest first" },
  { value: "created_asc",   label: "Oldest first" },
  { value: "updated_desc",  label: "Last updated" },
  { value: "name_asc",      label: "Business name A→Z" },
  { value: "name_desc",     label: "Business name Z→A" },
  { value: "city_asc",      label: "City A→Z" },
  { value: "city_desc",     label: "City Z→A" },
  { value: "state_asc",     label: "State A→Z" },
  { value: "state_desc",    label: "State Z→A" },
  { value: "assigned_user", label: "Assigned user" },
  { value: "status",        label: "Status" },
  { value: "category",      label: "Industry / Category" },
  { value: "emails_first",  label: "Leads with email first" },
  { value: "website_first", label: "Leads with website first" },
  { value: "phone_first",   label: "Leads with phone first" },
];

export const STATUS_CHIPS = [
  { value: "prospect",    label: "New / Prospect" },
  { value: "contacted",   label: "Contacted" },
  { value: "qualified",   label: "Qualified (Opportunity)" },
  { value: "closed_won",  label: "Closed Won" },
  { value: "closed_lost", label: "Closed Lost" },
];

const DATE_PRESETS = [
  { value: "today",     label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d",        label: "Last 7 days" },
  { value: "30d",       label: "Last 30 days" },
  { value: "90d",       label: "Last 90 days" },
  { value: "custom",    label: "Custom range" },
];

function presetToISO(preset) {
  if (!preset || preset === "custom") return { from: null, to: null };
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);
  if (preset === "today") start.setHours(0, 0, 0, 0);
  else if (preset === "yesterday") {
    start.setDate(start.getDate() - 1); start.setHours(0, 0, 0, 0);
    end.setDate(end.getDate() - 1); end.setHours(23, 59, 59, 999);
  } else if (preset === "7d")  start.setDate(start.getDate() - 7);
  else if (preset === "30d") start.setDate(start.getDate() - 30);
  else if (preset === "90d") start.setDate(start.getDate() - 90);
  return { from: start.toISOString(), to: end.toISOString() };
}

/**
 * Controlled advanced-filter bar.
 *
 *   filters: { sort_by, has_email, has_website, has_phone, unassigned,
 *              assigned_only, statuses[], state, city, zip_code,
 *              category, datePreset, created_from, created_to }
 */
export default function LeadFilterBar({ filters, onChange, onReset }) {
  const [locations, setLocations] = useState({ states: [], cities: [], zip_codes: [] });
  const [categories, setCategories] = useState([]);
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");

  useEffect(() => {
    fetch(`${API}/leads/distinct-locations`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then((d) => d && setLocations(d)).catch(() => {});
    fetch(`${API}/leads/categories`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then((d) => d && setCategories(d.categories || [])).catch(() => {});
    fetchViews();
  }, []);

  const fetchViews = () => {
    fetch(`${API}/leads/filter-views`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then((d) => d && setViews(d.views || [])).catch(() => {});
  };

  const set = (patch) => onChange({ ...filters, ...patch });

  const toggleStatus = (s) => {
    const current = new Set(filters.statuses || []);
    if (current.has(s)) current.delete(s); else current.add(s);
    set({ statuses: Array.from(current) });
  };

  const onDatePreset = (preset) => {
    if (preset === "custom") {
      set({ datePreset: preset });
      return;
    }
    const { from, to } = presetToISO(preset);
    set({ datePreset: preset, created_from: from, created_to: to });
  };

  const handleSavePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name"); return; }
    const payload = { name, filters };
    const r = await fetch(`${API}/leads/filter-views`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify(payload),
    });
    if (r.ok) {
      toast.success(`Saved "${name}"`);
      setPresetName(""); setSaveOpen(false);
      fetchViews();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || "Failed to save preset");
    }
  };

  const loadPreset = (v) => {
    onChange({ ...defaultFilters(), ...v.filters });
    toast.success(`Loaded "${v.name}"`);
  };

  const deletePreset = async (v) => {
    if (!window.confirm(`Delete "${v.name}"?`)) return;
    const r = await fetch(`${API}/leads/filter-views/${v.view_id}`, {
      method: "DELETE", credentials: "include",
    });
    if (r.ok) { toast.success("Deleted"); fetchViews(); }
  };

  const activeCount = useMemo(() => {
    let n = 0;
    if (filters.has_email != null) n++;
    if (filters.has_website != null) n++;
    if (filters.has_phone != null) n++;
    if (filters.has_social != null) n++;
    if (filters.unassigned) n++;
    if (filters.assigned_only) n++;
    if ((filters.statuses || []).length) n += filters.statuses.length;
    if (filters.state) n++;
    if (filters.city) n++;
    if (filters.zip_code) n++;
    if (filters.category) n++;
    if (filters.datePreset) n++;
    return n;
  }, [filters]);

  const citiesForState = useMemo(() => {
    return locations.cities; // org-wide; could be filtered later by state if needed
  }, [locations]);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="lead-filter-bar">
      {/* Sort */}
      <div className="flex items-center gap-2">
        <Label className="text-xs text-slate-600 hidden md:inline">Sort by</Label>
        <Select value={filters.sort_by || "created_desc"} onValueChange={(v) => set({ sort_by: v })}>
          <SelectTrigger className="w-[200px]" data-testid="lead-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Quick-filter chips */}
      <TooltipProvider>
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip
            active={filters.has_email === true}
            onClick={() => set({ has_email: filters.has_email === true ? null : true })}
            icon={Mail} label="With Email" testId="chip-with-email"
          />
          <Chip
            active={filters.has_email === false}
            onClick={() => set({ has_email: filters.has_email === false ? null : false })}
            icon={MailX} label="No Email" testId="chip-no-email"
          />
          <Chip
            active={filters.has_website === true}
            onClick={() => set({ has_website: filters.has_website === true ? null : true })}
            icon={Globe} label="With Website" testId="chip-with-website"
          />
          <Chip
            active={filters.has_website === false}
            onClick={() => set({ has_website: filters.has_website === false ? null : false })}
            icon={GlobeLock} label="No Website" testId="chip-no-website"
          />
          <Chip
            active={filters.has_phone === true}
            onClick={() => set({ has_phone: filters.has_phone === true ? null : true })}
            icon={PhoneIcon} label="With Phone" testId="chip-with-phone"
          />
          <Chip
            active={filters.has_social === true}
            onClick={() => set({ has_social: filters.has_social === true ? null : true })}
            icon={Share2} label="Has Social Media" testId="chip-with-social"
          />
          <Chip
            active={filters.has_social === false}
            onClick={() => set({ has_social: filters.has_social === false ? null : false })}
            icon={Share2} label="No Social Media" testId="chip-no-social"
          />
          <Chip
            active={filters.unassigned === true}
            onClick={() => set({ unassigned: !filters.unassigned, assigned_only: false })}
            icon={UserX} label="Unassigned" testId="chip-unassigned"
          />
          <Chip
            active={filters.assigned_only === true}
            onClick={() => set({ assigned_only: !filters.assigned_only, unassigned: false })}
            icon={UserCheck} label="Assigned" testId="chip-assigned"
          />
        </div>
      </TooltipProvider>

      {/* More filters popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" data-testid="more-filters-btn">
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            More filters
            {activeCount > 0 && <Badge variant="secondary" className="ml-2 h-5 px-1.5">{activeCount}</Badge>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-4 space-y-4" align="start">
          {/* Status multi-select */}
          <div>
            <Label className="text-xs text-slate-600">Status</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {STATUS_CHIPS.map((s) => {
                const on = (filters.statuses || []).includes(s.value);
                return (
                  <button
                    key={s.value} type="button"
                    onClick={() => toggleStatus(s.value)}
                    data-testid={`chip-status-${s.value}`}
                    className={
                      "px-2.5 py-1 rounded-full text-xs border transition " +
                      (on ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                         : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50")
                    }
                  >{s.label}</button>
                );
              })}
            </div>
          </div>

          {/* Location */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-slate-600">State</Label>
              <Select value={filters.state || "__all__"} onValueChange={(v) => set({ state: v === "__all__" ? "" : v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-state"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__all__">Any state</SelectItem>
                  {locations.states.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.value} ({s.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">City</Label>
              <Select value={filters.city || "__all__"} onValueChange={(v) => set({ city: v === "__all__" ? "" : v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-city"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__all__">Any city</SelectItem>
                  {citiesForState.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.value} ({c.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Zip</Label>
              <Input
                value={filters.zip_code || ""}
                onChange={(e) => set({ zip_code: e.target.value })}
                placeholder="Any"
                className="h-8 text-xs"
                data-testid="filter-zip"
              />
            </div>
          </div>

          {/* Industry / Category */}
          <div>
            <Label className="text-xs text-slate-600">Industry / Category</Label>
            <Select value={filters.category || "__all__"} onValueChange={(v) => set({ category: v === "__all__" ? "" : v })}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-category"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="__all__">Any industry</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.category} value={c.category}>{c.category} ({c.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date filter */}
          <div>
            <Label className="text-xs text-slate-600 flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5" /> Created
            </Label>
            <Select value={filters.datePreset || "__any__"} onValueChange={(v) => onDatePreset(v === "__any__" ? null : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-date-preset"><SelectValue placeholder="Any time" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any time</SelectItem>
                {DATE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {filters.datePreset === "custom" && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  type="date" className="h-8 text-xs"
                  value={(filters.created_from || "").substring(0, 10)}
                  onChange={(e) => set({ created_from: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  data-testid="filter-date-from"
                />
                <Input
                  type="date" className="h-8 text-xs"
                  value={(filters.created_to || "").substring(0, 10)}
                  onChange={(e) => set({ created_to: e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : null })}
                  data-testid="filter-date-to"
                />
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Saved views menu */}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" data-testid="saved-views-btn">
            <Bookmark className="w-4 h-4 mr-2" />
            Saved views
            {views.length > 0 && <Badge variant="secondary" className="ml-2 h-5 px-1.5">{views.length}</Badge>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-2" align="start">
          <div className="space-y-1 max-h-72 overflow-auto">
            {views.length === 0 && (
              <div className="text-xs text-slate-500 px-2 py-3 text-center">
                No saved views yet. Tweak filters then "Save current".
              </div>
            )}
            {views.map((v) => (
              <div key={v.view_id} className="flex items-center justify-between gap-2 rounded hover:bg-slate-50 p-1">
                <button
                  className="flex-1 text-left text-sm px-2 py-1 truncate"
                  onClick={() => loadPreset(v)}
                  data-testid={`load-view-${v.view_id}`}
                  title={v.name}
                >{v.name}</button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => deletePreset(v)} data-testid={`delete-view-${v.view_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <div className="border-t pt-2 mt-2">
            <Button variant="default" size="sm" className="w-full" onClick={() => setSaveOpen(true)} data-testid="save-view-btn">
              <Save className="w-4 h-4 mr-2" /> Save current filters
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Reset */}
      {activeCount > 0 && (
        <Button variant="ghost" size="sm" onClick={onReset} data-testid="reset-filters-btn">
          <X className="w-4 h-4 mr-1" /> Clear ({activeCount})
        </Button>
      )}

      {/* Save preset dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Save filter view</DialogTitle>
            <DialogDescription>
              Save the current sort + filter combination so you can apply it with one click later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              autoFocus
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g. Pennsylvania Psychiatrists"
              data-testid="save-view-name"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePreset} data-testid="save-view-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Chip({ active, onClick, icon: Icon, label, testId }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          data-testid={testId}
          className={
            "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition " +
            (active ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                   : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50")
          }
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function defaultFilters() {
  return {
    sort_by: "created_desc",
    has_email: null, has_website: null, has_phone: null, has_social: null,
    unassigned: false, assigned_only: false,
    statuses: [],
    state: "", city: "", zip_code: "",
    category: "",
    datePreset: null, created_from: null, created_to: null,
  };
}

export function filtersToParams(f) {
  const p = new URLSearchParams();
  if (f.sort_by && f.sort_by !== "created_desc") p.append("sort_by", f.sort_by);
  if (f.has_email != null) p.append("has_email", String(f.has_email));
  if (f.has_website != null) p.append("has_website", String(f.has_website));
  if (f.has_phone != null) p.append("has_phone", String(f.has_phone));
  if (f.has_social != null) p.append("has_social", String(f.has_social));
  if (f.unassigned) p.append("unassigned", "true");
  if (f.assigned_only) p.append("assigned_only", "true");
  if (f.statuses && f.statuses.length) p.append("status", f.statuses.join(","));
  if (f.state) p.append("state", f.state);
  if (f.city) p.append("city", f.city);
  if (f.zip_code) p.append("zip_code", f.zip_code);
  if (f.category) p.append("category", f.category);
  if (f.created_from) p.append("created_from", f.created_from);
  if (f.created_to) p.append("created_to", f.created_to);
  return p;
}
